/**
 * Product entity — each price point is its own orderable product (pilot model, ТЗ §4).
 *
 * Mirrors the backend ``ProductListSerializer`` / ``ProductDetailSerializer``. There are no
 * nested variants — the customer orders the product directly. Money fields arrive as
 * strings (DRF ``Decimal``); they are never re-derived on the client.
 */

/** Server-validated sort modes (must match ``apps.catalog.selectors.PRODUCT_SORTS``). */
export type ProductSort =
  | "price_asc"
  | "price_desc"
  | "popularity"
  | "rating"
  | "newest";

/** Ordered sort options with Russian labels for the shop's sort control. */
export const PRODUCT_SORT_OPTIONS: readonly {
  readonly value: ProductSort;
  readonly label: string;
}[] = [
  { value: "newest", label: "Сначала новые" },
  { value: "popularity", label: "Популярные" },
  { value: "rating", label: "По рейтингу" },
  { value: "price_asc", label: "Сначала дешевле" },
  { value: "price_desc", label: "Сначала дороже" },
] as const;

export const DEFAULT_PRODUCT_SORT: ProductSort = "newest";

/** Compact product as returned by the list endpoint (mirrors ``ProductListSerializer``). */
export interface ProductListItem {
  readonly id: number;
  readonly game: number;
  readonly category: number;
  readonly title: string;
  readonly image_url: string;
  readonly eta_text: string;
  /** RUB, DRF Decimal string. */
  readonly base_price: string;
  /** Per-product RUB cap on paying with primogems; null = inherit category/global. */
  readonly primogem_cap: string | null;
  readonly star_glitter_allowed: boolean;
  /** Per-order quantity cap; the cart stepper blocks «+» at this limit (ТЗ §6). */
  readonly max_qty_per_order: number;
  /** Denormalised rating average, DRF Decimal string (e.g. "4.70"). */
  readonly rating_avg: string;
  readonly reviews_count: number;
  readonly orders_count: number;
}

/** Full product detail (catalog card — ТЗ §5; no variants in the pilot). */
export interface ProductDetail {
  readonly id: number;
  readonly game: number;
  readonly category: number;
  readonly title: string;
  readonly description: string;
  readonly whats_included: string;
  readonly image_url: string;
  readonly eta_text: string;
  readonly base_price: string;
  /** Per-product RUB cap on paying with primogems; null = inherit category/global. */
  readonly primogem_cap: string | null;
  readonly star_glitter_allowed: boolean;
  /** Per-order quantity cap; the cart stepper blocks «+» at this limit (ТЗ §6). */
  readonly max_qty_per_order: number;
  readonly rating_avg: string;
  readonly reviews_count: number;
  readonly orders_count: number;
}
